import { useQuery } from "@tanstack/react-query"
import { Printer } from "lucide-react"
import { useState } from "react"
import { createPortal } from "react-dom"

import { api } from "@/api/cliente"
import { CARNET, SEPARACION_CARAS } from "@/componentes/carnet/medidas"
import { VistaCarnet } from "@/componentes/carnet/VistaCarnet"
import type { Empresa } from "@/componentes/empresa/tipos"
import { cabenPorHoja } from "@/componentes/impresos/hoja"
import { PliegoImpresion } from "@/componentes/impresos/PliegoImpresion"
import type { Persona } from "@/componentes/personas/persona"
import { useFotoPersona } from "@/componentes/personas/useFotoPersona"
import { Boton } from "@/componentes/ui/Boton"

/**
 * El carnet corporativo de un empleado, dentro de su ficha.
 *
 * ⚠ **Vive en la ficha y no en su propia pantalla.** Fue la entrada «Carnets», donde se
 * elegía a alguien de una lista para verle el gafete; esa lista es la de «Personas» y el
 * permiso el mismo, así que era una vista de la ficha con otra puerta. La entrada la
 * retiró la migración `a91d4c2e7f60`, y `/carnets` lleva a «Personas».
 *
 * ⚠ **No guarda NADA.** Se arma al pedirlo con la ficha y la configuración de la
 * empresa. Guardado, sería el nombre, el cargo y la cédula de alguien congelados el día
 * que se imprimió, y al primer ascenso habría dos respuestas a «qué cargo tiene».
 *
 * ⚠ **La foto es la de la ficha, y se sube arriba**, en «Subir foto». No hay foto de
 * carnet: con dos habría dos retratos de la misma persona y ninguna forma de saber cuál
 * es el bueno.
 *
 * ⚠ **Sin foto no se imprime, y el aviso va en la PANTALLA**, no dentro de la pieza: lo
 * que se dibuja dentro se imprime, y acabaría plastificado.
 *
 * ⚠ **Sólo a un empleado ACTIVO.** A quien egresó o está suspendido no se le imprime:
 * sería entregar una credencial de la agencia a quien no está trabajando en ella.
 *
 * ⚠ **El pliego va en `document.body`, fuera de la ficha.** Al imprimir se esconden la
 * ficha lateral y la lista de «Personas» (`.no-print`); si el pliego fuera hijo de la
 * ficha, se escondería con ella y la hoja saldría en blanco.
 */
export function CarnetDePersona({ persona }: { persona: Persona }) {
  const [copias, setCopias] = useState(1)
  const empresa = useQuery({
    queryKey: ["empresa"],
    queryFn: () => api.get<Empresa>("/api/v1/empresa"),
  })
  const foto = useFotoPersona(persona)

  if (persona.estado !== "ACTIVO") {
    return (
      <section>
        <h3 className="rotulo mb-3">Carnet</h3>
        <p className="superficie p-3 text-sm" style={{ color: "var(--fg-muted)" }}>
          Sólo se imprime a quien está activo: a quien egresó o está suspendido sería entregarle una
          credencial de la agencia.
        </p>
      </section>
    )
  }

  const conReverso = Boolean(persona.contacto_emergencia || persona.telefono_emergencia)
  // ⚠ **La pieza que se repite en la hoja es el carnet ENTERO, con sus dos caras al
  // lado.** Contando con los 54 mm de una cara, la pantalla decía «9 por hoja» y
  // salían 3: con reverso la pieza mide 112 mm y sólo entra una columna.
  const anchoPieza = conReverso ? CARNET.ancho * 2 + SEPARACION_CARAS : CARNET.ancho
  const porHoja = cabenPorHoja(anchoPieza, CARNET.alto)
  const pieza = (
    <VistaCarnet
      persona={persona}
      empresa={empresa.data}
      foto={foto.data ?? null}
      conReverso={conReverso}
    />
  )

  return (
    <section data-testid="carnet-persona">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h3 className="rotulo flex-1">Carnet</h3>
        <span className="text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
          {CARNET.ancho} × {CARNET.alto} mm · {porHoja} por hoja
        </span>
      </div>

      {/* A escala real (1 mm = 1 mm) y con desplazamiento si no cabe, en vez de
          encogerlo: lo que se ve tiene que ser lo que se imprime. */}
      <div
        className="overflow-auto rounded-[var(--radius-tarjeta)] p-4"
        style={{ backgroundColor: "var(--surface-elevated)" }}
      >
        <div className="pieza-impresa-previa" style={{ width: "fit-content" }}>
          {pieza}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-end gap-3">
        <label className="grid gap-1">
          <span className="text-xs font-medium">Copias</span>
          <input
            type="number"
            min={1}
            max={50}
            className="campo h-9 w-24 text-sm tabular-nums"
            value={copias}
            onChange={(e) => setCopias(Math.min(50, Math.max(1, Number(e.target.value) || 1)))}
          />
        </label>
        <Boton variante="primario" disabled={!persona.tiene_foto} onClick={() => window.print()}>
          <Printer size={15} aria-hidden={true} />
          Imprimir carnet
        </Boton>
      </div>

      {!persona.tiene_foto && (
        <p role="alert" className="mt-2 text-sm" style={{ color: "var(--fg-muted)" }}>
          Falta la foto: súbela arriba, en «Subir foto». Un carnet sin retrato no identifica a
          nadie, así que no se imprime hasta tenerla.
        </p>
      )}
      {!conReverso && (
        <p className="mt-2 text-xs" style={{ color: "var(--fg-muted)" }}>
          Sin contacto de emergencia sale sin reverso, que es la única razón por la que el carnet lo
          lleva. Se completa arriba, en la ficha.
        </p>
      )}
      <p className="mt-3 text-xs" style={{ color: "var(--fg-muted)" }}>
        En el diálogo, deja la escala en <strong>100 %</strong> y activa los gráficos de fondo. Las
        dos caras salen juntas: se recortan y se pegan espalda contra espalda antes de plastificar.
      </p>

      {persona.tiene_foto &&
        createPortal(
          <PliegoImpresion pieza={pieza} copias={copias} porHoja={porHoja} />,
          document.body,
        )}
    </section>
  )
}
